// ============================================================
// Sidebar — 248px fixed-width on desktop; ≤900px it leaves the flow and
// becomes a fixed overlay (the `.open` class controls visibility, the CSS
// handles the slide animation). This component is the wiring; the layout
// rules live in global.css.
// ============================================================
import type { FeatureKey } from '@risma/data-client';
import type { Lang, NotificationItem, Tenant } from '../data/types';
import { Icon, type IconName } from '../components/Icon';
import { initials } from '../components/helpers';
import { useDashboard, useLogout } from '../data/context';
import { t } from '../i18n/t';
import type { Route } from './routes';

interface SidebarProps {
  lang: Lang;
  route: Route;
  onNavigate: (next: Route) => void;
  open: boolean;
  onClose: () => void;
  /** Desktop: collapse to an icon-only rail. */
  collapsed: boolean;
  onToggleCollapse: () => void;
  tenant: Tenant;
  notifications: NotificationItem[];
}

interface NavItem {
  id: Route;
  icon: IconName;
  labelKey: string;
  badge?: number;
  /**
   * Optional feature gate. If set, the item only renders when
   * `features[feature]` is true. Items without a gate always render.
   */
  feature?: FeatureKey;
}

interface NavGroup {
  labelKey: string;
  items: NavItem[];
}

export function Sidebar({
  lang,
  route,
  onNavigate,
  open,
  onClose,
  collapsed,
  onToggleCollapse,
  tenant,
  notifications,
}: SidebarProps) {
  const unread = notifications.filter((n) => !n.read).length;
  const logout = useLogout();
  const { features } = useDashboard();

  const groups: NavGroup[] = [
    {
      labelKey: 'nav_service',
      items: [
        { id: 'cal', icon: 'calendar', labelKey: 'cal', feature: 'reservations' },
        { id: 'bookings', icon: 'list', labelKey: 'bookings', feature: 'reservations' },
        { id: 'notifications', icon: 'bell', labelKey: 'notifications', badge: unread, feature: 'notifications' },
      ],
    },
    {
      labelKey: 'nav_clients',
      items: [{ id: 'customers', icon: 'users', labelKey: 'customers', feature: 'customers' }],
    },
    {
      labelKey: 'nav_settings',
      items: [
        { id: 'insights', icon: 'chart', labelKey: 'insights', feature: 'insights' },
        { id: 'availability', icon: 'sliders', labelKey: 'availability', feature: 'availability' },
        { id: 'tables', icon: 'utensils', labelKey: 'tables', feature: 'availability' },
        { id: 'website', icon: 'globe', labelKey: 'website', feature: 'website' },
        { id: 'settings', icon: 'gear', labelKey: 'settings', feature: 'settings' },
      ],
    },
  ];

  // Filter each group's items by feature flag, then drop any empty groups.
  const visibleGroups = groups
    .map((g) => ({
      ...g,
      items: g.items.filter((it) => !it.feature || features[it.feature]),
    }))
    .filter((g) => g.items.length > 0);

  return (
    <>
      {open && <div className="scrim-side" onClick={onClose} aria-hidden="true" />}
      <aside className={'side' + (open ? ' open' : '') + (collapsed ? ' collapsed' : '')}>
        <button
          type="button"
          className="side-collapse"
          onClick={onToggleCollapse}
          aria-label={collapsed ? 'Espandi menu' : 'Riduci menu'}
          title={collapsed ? 'Espandi' : 'Riduci'}
        >
          {collapsed ? '›' : '‹'}
        </button>
        <div className="side-brand">
          {/* Targa, non bottone: niente caret/hover finché non esiste un menu
              vero (es. multi-sede in futuro). */}
          <div className="tenant">
            <span className="tenant-logo" aria-hidden="true">
              {tenant.mono}
            </span>
            <span className="tenant-meta">
              <span className="tenant-name">{tenant.name}</span>
              <span className="tenant-sub">{tenant.sub}</span>
            </span>
          </div>
        </div>

        <nav className="side-nav" aria-label="Primary">
          {visibleGroups.map((g) => (
            <div className="nav-group" key={g.labelKey}>
              <div className="nav-group-label">{t(g.labelKey, lang)}</div>
              {g.items.map((it) => (
                <button
                  key={it.id}
                  type="button"
                  className={'nav-item' + (route === it.id ? ' active' : '')}
                  aria-current={route === it.id ? 'page' : undefined}
                  onClick={() => onNavigate(it.id)}
                >
                  <Icon name={it.icon} size={17} className="ico" />
                  <span>{t(it.labelKey, lang)}</span>
                  {it.badge ? (
                    <span className="nav-badge" aria-live="polite">
                      {it.badge}
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
          ))}
        </nav>

        <div className="side-foot">
          <div className="side-user-row">
            <span className="avatar a1" aria-hidden="true">
              {initials(tenant.owner)}
            </span>
            <span className="side-user-meta">
              <span className="side-user-name">{tenant.owner}</span>
              <span className="side-user-role">{tenant.ownerRole[lang]}</span>
            </span>
            {logout && (
              <button
                type="button"
                className="side-logout"
                onClick={() => {
                  void logout();
                }}
                aria-label="Esci / Sign out"
                title="Esci"
              >
                <Icon name="logout" size={15} />
              </button>
            )}
          </div>
          <div className="powered">
            <span>{t('poweredNote', lang)}</span> <b>Risma</b>
            <i>studio</i>
          </div>
        </div>
      </aside>
    </>
  );
}
